import axios, { AxiosError } from "axios";
import { CeleryWorkflowStatus } from "gen/model/celeryWorkflowStatus";
import { UserRoleTypeEnum } from "gen/model/userRoleTypeEnum";
import i18n from "i18n/config";
import type { TFunction } from "i18next";
import { get, omitBy } from "lodash-es";
import {
  FieldError,
  FieldErrors,
  FieldPath,
  UseFormReturn,
} from "react-hook-form";
import {
  Field,
  FieldRefs,
  FieldValues,
} from "react-hook-form/dist/types/fields";
import { NavErrorForbidden } from "shared/navigation/NavErrors";
import { isNotUndefined } from "shared/utils/ArrayUtils";
import { isDev } from "shared/utils/DevUtils";
import { useUserRoles } from "shared/utils/PermissionUtils";
import { isTruthy } from "shared/utils/utils";

export type ApiErrorData =
  | {
      [fieldName: string]: string[] | ApiErrorData;
    }
  | ApiErrorData[]
  | string[];

export interface ApiError {
  code: string;
  message: string;
  data?: ApiErrorData;
  dev_message?: string;
}

interface ErrorResponse {
  error?: ApiError;
}

export const getApiErrorFromResponse = (
  error: unknown,
): ApiError | undefined => {
  const axiosError = error as AxiosError | null | undefined;
  if (!axiosError?.response) {
    return undefined;
  }
  const data = axiosError.response.data as ErrorResponse;
  if (!data.error) {
    return;
  }
  return data.error;
};

const isArrayOfAllStrings = (array: unknown[]): array is string[] =>
  array.every((it) => typeof it === "string");

const joinPrefix = (prefix: string | undefined, fieldName: string | number) =>
  [prefix, fieldName].filter(isNotUndefined).join(".");

export interface FlatFormFieldError {
  fieldPath: string;
  error: string;
}

export const flattenApiErrorData = (
  errors: ApiErrorData | undefined,
  prefix?: string,
): FlatFormFieldError[] => {
  if (!errors) {
    return [];
  }
  if (Array.isArray(errors)) {
    if (isArrayOfAllStrings(errors)) {
      return [{ fieldPath: prefix!, error: errors.join(", ") }];
    }
    return errors.flatMap((nestedError, index) =>
      flattenApiErrorData(nestedError, joinPrefix(prefix, index)),
    );
  }
  return Object.entries(errors).flatMap(([fieldName, nestedError]) =>
    flattenApiErrorData(nestedError, joinPrefix(prefix, fieldName)),
  );
};

export const setFormFieldErrors = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  errors: FlatFormFieldError[],
) => {
  errors.forEach((error) => {
    if (isFieldPathValidFormInput(form, error.fieldPath)) {
      form.setError(error.fieldPath as FieldPath<TFieldValues>, {
        message: error.error,
      });
    }
  });
};

const isErrorDataResponse = (
  errorData: unknown,
): errorData is Required<ErrorResponse> =>
  !!errorData && typeof errorData === "object" && "error" in errorData;

export const getApiError = (error: unknown): ApiError | undefined => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data;
    if (isErrorDataResponse(errorData)) {
      return errorData.error;
    }
  }
  return undefined;
};

export const getErrorMessage = (t: TFunction<"common"[]>, error: unknown) => {
  if (typeof error === "string") {
    return error;
  }
  const apiError = getApiError(error);
  if (apiError) {
    return apiError.dev_message
      ? [
          apiError.message,
          "",
          `[${t("common:internalErrorDetails")}]`,
          apiError.dev_message,
        ].join("\n")
      : apiError.message;
  }
  if (error instanceof NavErrorForbidden) {
    return t("common:error.forbidden");
  }
  if (error instanceof Error) {
    return error.message;
  }
  return t("common:error.generic");
};

export const getInternalErrorDetails = (error: unknown) => {
  if (typeof error === "string") {
    return error;
  }
  const details = [];

  if (error instanceof Error) {
    if (error.stack && !axios.isAxiosError(error)) {
      details.push(i18n.t("common:error.generic"), error.stack);
    } else {
      details.push(i18n.t("common:error.generic"), error.toString());
    }
  }
  const apiErrorData = getApiError(error);
  if (apiErrorData) {
    details.push("Code: " + apiErrorData.code);
    if (apiErrorData.dev_message) {
      details.push("Message: " + apiErrorData.dev_message);
    }
    if (apiErrorData.data) {
      details.push("Data: " + JSON.stringify(apiErrorData.data));
    }
  }
  if (details.length === 0) {
    details.push(JSON.stringify(error));
  }
  return details.join("\n");
};

const isFieldError = (error: unknown): error is FieldError =>
  !!error && typeof error === "object" && "message" in error;

export const flattenFormFieldErrors = <TFieldValues extends FieldValues>(
  error: FieldErrors<TFieldValues>,
  prefix?: string,
): FlatFormFieldError[] => {
  if (typeof error !== "object") {
    return [];
  }
  if (Array.isArray(error)) {
    return (error as FieldErrors<TFieldValues>[]).flatMap(
      (nestedError, index) =>
        flattenFormFieldErrors(nestedError, joinPrefix(prefix, index)),
    );
  }
  const flatErrors: FlatFormFieldError[] = [];
  if (isFieldError(error)) {
    flatErrors.push({ fieldPath: prefix!, error: error.message! });
  }
  const flatChildErrors = Object.entries(
    error as Record<string, FieldErrors<TFieldValues>>,
  ).flatMap(([fieldName, nestedError]) =>
    flattenFormFieldErrors(nestedError, joinPrefix(prefix, fieldName)),
  );
  flatErrors.push(...flatChildErrors);
  return flatErrors;
};

/**
 * Check that the field path represents a valid form input, meaning it exists
 * and is not a hidden field or a field array.
 */
export const isFieldPathValidFormInput = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  fieldPath: string,
): boolean => {
  const field = get(form.control._fields, fieldPath);
  return Boolean(
    field &&
      !Array.isArray(field) &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      field._f &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      field._f.ref?.type !== "hidden",
  );
};

export const getFormFieldNames = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
): string[] => {
  const isField = (field: Field | FieldRefs): field is Field => "_f" in field;
  const visit = (fields: Field | FieldRefs): string[] =>
    isField(fields)
      ? [fields._f.name]
      : Object.values(fields).filter(isTruthy).flatMap(visit);
  return visit(form.control._fields);
};

export const throwExpression = (error: unknown): never => {
  throw error;
};

export const useShowInternalErrorDetails = () => {
  const userRoles = useUserRoles();
  return (
    isDev ||
    userRoles.includes(UserRoleTypeEnum.service) ||
    userRoles.includes(UserRoleTypeEnum.service_admin)
  );
};

const getStringBeforeSubstring = (str: string, substring: string): string => {
  const index = str.indexOf(substring);
  if (index === -1) {
    // Substring not found, return the whole string
    return str;
  }
  return str.slice(0, index);
};

export const getErrorMessageFromCeleryWorkflowMessage = (
  error_message: string,
) => {
  // Strip off the Request ID that is appended to the error message
  const message = getStringBeforeSubstring(error_message, "Request ID");

  if (!message.startsWith("Dependency")) {
    return message;
  }

  // extracts test in single quotes
  // Example: Dependency 66521a8a-bfb4-4877-95ae-dbb9cc7a2cda raised MaybernError('tmp') Request ID: 645c3ed8-e543-443d-8aea-b55c9d4e7393
  // --> we want to extract 'tmp'
  const matches = error_message.match(/'([^']+)'/);
  return matches ? matches[1] : "";
};

export const getErrorMessageFromCeleryWorkflow = (
  celery_workflow: CeleryWorkflowStatus | undefined,
): string => {
  if (!celery_workflow?.error_message) {
    return "";
  }

  return getErrorMessageFromCeleryWorkflowMessage(
    celery_workflow.error_message,
  );
};

export const filterExcludedNonFieldErrors = (
  errorData: object,
  excludeNonFieldErrors: (keyof FieldErrors<never>)[] | undefined,
) =>
  omitBy(
    errorData,
    (_, key) =>
      excludeNonFieldErrors?.includes(key) ||
      excludeNonFieldErrors?.some((e) => key.startsWith(e.toString())),
  );
